import React, { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { publicAPI } from "../api/publicAPI";
import { processSteps } from "../mock"; // Fallback

const Process = () => {
  const { t, currentLanguage } = useLanguage();
  const [processContent, setProcessContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProcessContent();
  }, [currentLanguage]);

  const loadProcessContent = async () => {
    try {
      setLoading(true);
      const content = await publicAPI.getPageContent('process', currentLanguage);
      setProcessContent(content);
    } catch (err) {
      console.error("Failed to load process content from API, using fallback:", err);
      setProcessContent(null);
    } finally {
      setLoading(false);
    }
  };

  const steps = processContent?.steps || processSteps;
  const title = processContent?.title || t('collaborationSteps');

  if (loading) {
    return (
      <section id="process" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse text-center mb-16">
            <div className="h-8 bg-gray-300 rounded w-1/2 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/3 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="process" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('processDescription')}
          </p>
        </div>

        {/* Process steps - unified style with arrows */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.slice(0, 8).map((step, index) => (
            <div key={index} className="relative">
              {/* Step card */}
              <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6 h-full flex flex-col">
                {/* Step number badge */}
                <div className="flex items-center justify-center mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-xl font-bold text-white">
                      {index + 1}
                    </span>
                  </div>
                </div>

                {/* Step title */}
                <h3 className="text-base font-bold text-gray-900 mb-3 text-center min-h-[48px] flex items-center justify-center">
                  {step.title}
                </h3>

                {/* Step description */}
                <p className="text-gray-600 text-sm text-center leading-relaxed flex-grow">
                  {step.description}
                </p>
              </div>

              {/* Arrow connectors */}
              {index < 7 && (
                <>
                  {/* Desktop arrows - horizontal after every 4th item, vertical otherwise */}
                  <div className="hidden lg:block absolute z-10">
                    {/* Right arrow (items 0-2, 4-6) */}
                    {(index < 3 || (index >= 4 && index < 7)) && (
                      <div className="absolute -right-3 top-1/2 transform -translate-y-1/2">
                        <ArrowRight className="h-6 w-6 text-orange-500" />
                      </div>
                    )}
                    {/* Down arrow after 3rd item (goes to 4th) */}
                    {index === 3 && (
                      <div className="absolute left-1/2 -bottom-3 transform -translate-x-1/2 rotate-90">
                        <ArrowRight className="h-6 w-6 text-orange-500" />
                      </div>
                    )}
                  </div>

                  {/* Mobile/Tablet arrows - always down */}
                  <div className="lg:hidden flex justify-center my-3">
                    <ArrowRight className="h-6 w-6 text-orange-500 rotate-90" />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Info note */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm">
            {currentLanguage === 'RU' 
              ? 'Каждый этап тщательно контролируется нашими специалистами' 
              : 'Each stage is carefully monitored by our specialists'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Process;
